import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { parseISO, isValid as isValidDate, formatISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { DatePicker } from '@atlaskit/datetime-picker';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import messages from '@atlassian/help-center-common-component/messages';
import dateNow from '@atlassian/help-center-common-util/date-now';
import { format, getWeekStartDay } from '@atlassian/help-center-common-util/date-picker-utils';
import { datePickerStyles } from './styled';

export interface DatePickerProps extends FieldFormProps<string>, InjectedIntlProps {
    dateFormat?: string;
    defaultValue?: string;
    firstDay?: number;
    useISO8601WeekNumbers?: boolean;
    locale: string;
}

const IsoDateFormat = /^\d{4}-\d{2}-\d{2}$/;

// TODO: Add proper locale support so that this workaround to fix date parsing isn't needed
const parseInputValue = (date: string): Date => {
    if (isValidDate(new Date(date))) {
        return parseISO(new Date(date).toISOString());
    }
    return parseISO(new Date().toISOString());
};

export class CustomDatePicker extends React.PureComponent<DatePickerProps> {
    onChange = (isoTime: string) => {
        const { onChange } = this.props;
        onChange && onChange(this.props.id, isoTime);
    };

    parseDateLabel = (isoTime: string, dateFormat: string): string => {
        return isoTime && dateFormat ? format(parseISO(isoTime), dateFormat) : isoTime;
    };

    render() {
        const {
            id,
            label,
            required,
            defaultValue,
            description,
            errorMessage,
            disabled,
            locale,
            firstDay,
            useISO8601WeekNumbers,
            intl,
            dateFormat = 'yyyy-MM-dd',
        } = this.props;
        const isInvalid = !isEmpty(errorMessage);
        // Need to refer to id like this.
        // See: https://atlaskit.atlassian.com/packages/core/datetime-picker
        const inputId = `react-select-${id}-input`;
        let defaultDateValue = undefined;
        if (defaultValue && isValidDate(new Date(defaultValue))) {
            // Fix for the one-day-off issue with new Date()
            // Link: [DateTime Picker One Day Off Issue](https://hello.atlassian.net/wiki/x/zwTjxw)
            defaultDateValue = defaultValue.match(IsoDateFormat)
                ? formatISO(new Date(defaultValue.replace(/-/g, '/')))
                : formatISO(new Date(defaultValue));
        }

        return (
            <FieldBase
                htmlFor={inputId}
                label={label}
                required={required}
                description={description}
                errorMessage={errorMessage}
            >
                <DatePicker
                    id={id}
                    name={id}
                    locale={locale}
                    defaultValue={defaultDateValue}
                    onChange={this.onChange}
                    isInvalid={isInvalid}
                    innerProps={datePickerStyles}
                    isDisabled={!!disabled}
                    formatDisplayLabel={this.parseDateLabel}
                    placeholder={intl.formatMessage(messages.forExample, { text: format(dateNow(), dateFormat) })}
                    dateFormat={dateFormat}
                    weekStartDay={getWeekStartDay(firstDay, useISO8601WeekNumbers)}
                    parseInputValue={parseInputValue}
                />
            </FieldBase>
        );
    }
}

export default injectIntl(CustomDatePicker);
